/* eslint-disable eqeqeq, react/prop-types, @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';

import { Button, Input, Modal } from 'antd';
import cloneDeep from 'lodash/cloneDeep';

import {
  totangoEventTracking,
  totangoSetAccountAttributes
} from '#analytics/implements/totango/index.js';
import calendarModificationIconModal from '#assets/img/activitymodification/calendar-modal.png';
import CardTask from '#components/CardTask/index.js';
import ModalMessage from '#components/ExportablePDFMessage/ModalMessage.jsx';
import LookaheadExportPdfModal from '#components/ExportPdfModal/LookaheadExportPdfModal/LookaheadExportPdfModal.jsx';
import ConstraintForm from '#components/LookAhead/Constraints/ConstraintForm/index.js';
import ModalAddResources from '#components/ModalAddResources/index.js';
import ModalAddSubContract from '#components/Settings/ModalAddSubContract/index.js';
import ModalAddTags from '#components/Settings/ModalAddTags/index.js';
import { userActions } from '#redux/actions/userActions.js';
import { base } from '#services/base.js';
import { activityModificationService } from '#services/index.js';
import { socket } from '#services/socket.service.js';
import { taskService } from '#services/task.service.js';
import {
  belongsToRange,
  excludeDeep,
  getTodayWeekWithPday,
  handshake,
  openNotification
} from '#utils/index.js';
import {
  defineDateUnfit,
  defineTitleModification,
  getTask
} from '#utils/lookahead-common.js';
import { getSignedUser } from '#utils/userUtils.js';
import { useDispatch, useSelector } from 'react-redux';
import './index.scss';
import { ReportModal } from './ReportModal.tsx';

const { TextArea } = Input;

export function Modals({
  activities,
  activityResource,
  antIcon,
  cardState,
  currentActivity,
  currentTask,
  dateRange,
  defaultView,
  deleteSubcontract,
  gantt,
  groupBy,
  handlePopVisibility,
  handlePopVisibilityCloseAll,
  history,
  isExportPdfModalVisible,
  lastLevelActivitiesCard,
  modalModification,
  modelReportLookAhead,
  onCloseCard,
  permission,
  popsVisibility,
  refreshResourceForGantt,
  resourcesCard,
  setIsExportPdfModalVisible,
  setLastLevelActivitiesCard,
  setModalModification,
  setModelReportLookAhead,
  setRefreshResourceForGantt,
  setReloadTableUsers,
  setSubcontractsReload,
  setVisibleForm,
  setVisibleFormResource,
  setVisibleFormSubcontract,
  setVisibleFormTags,
  subContracts,
  t,
  tableConfig,
  taskSelected,
  toSelectResponsables,
  typeResource,
  updateAsyncActivity,
  updateAsyncTask,
  updateRender,
  updateResources,
  visibleForm,
  visibleFormResource,
  visibleFormSubcontract,
  visibleFormTags
}) {
  const dispatch = useDispatch();
  const projectState: any = useSelector<any>((state) => state.projectState);

  const createActivityModificationRequest = async (task, activity) => {
    const loggedUser = getSignedUser();
    if (!loggedUser) return;
    const projectId = projectState.projectSelected;
    const sectorId = projectState.sectorSelected;
    const link = base.front + 'masterplan/' + sectorId + '/' + projectId;

    const activityModification = {
      type: task.unfitType,
      state: 'waiting',
      description: modalModification.description,
      activityId: activity.id,
      userRequestId: loggedUser.id,
      startOriginal: activity.start_date,
      endOriginal: activity.end_date,
      user: loggedUser,
      projectId,
      sectorId,
      link,
      startNew: null,
      endNew: null
    };

    if (task.unfitType === 'start') {
      activityModification.startNew = task.start_date;
      activityModification.endNew = activity.end_date;
    } else if (task.unfitType === 'end') {
      activityModification.startNew = activity.start_date;
      activityModification.endNew = task.end_date;
    } else if (task.unfitType === 'both') {
      activityModification.startNew = task.start_date;
      activityModification.endNew = task.end_date;
    }
    const res = await activityModificationService.create(activityModification);
    dispatch(userActions.setUserUpdate());
    if (res) {
      openNotification({
        type: 'success',
        title: t('success_request_notify')
      });
      setModalModification({
        ...modalModification,
        visible: false
      });
    }
  };

  const companyState: any = useSelector<any>((state) => state.companyState);
  const [currentWeekWithPday, setCurrentWeekWithPday] = useState();

  useEffect(() => {
    const loggedUser = getSignedUser();
    const currentCompany = companyState?.currentCompany;
    const project = projectState.allProjects.find(
      (p) => p.id === projectState.projectSelected
    );

    if (!loggedUser) return;

    totangoSetAccountAttributes(
      loggedUser,
      projectState.projectSelected,
      currentCompany?.name,
      currentCompany?.id,
      project?.name,
      project?.stage,
      project?.country
    );

    totangoEventTracking(
      `p_${projectState.projectSelected}`,
      loggedUser,
      'See Lookahead',
      'Lookahead'
    );

    const todayWeek = getTodayWeekWithPday(projectState);
    if (!todayWeek) return;
    setCurrentWeekWithPday(todayWeek);

    window.Appcues.page();
  }, []);

  const [isShowFitToOnePageModal, setIsShowFitToOnePageModal] = useState(false);

  const handleUpdatedTasks = async (updatedTasks) => {
    if (!updatedTasks || !updatedTasks.length) return;

    const asyncFeature = updatedTasks.map(async (t) => {
      let updatedTask = await taskService.show(t.id);
      if (!updatedTask) return;

      updatedTask = updatedTask.task;
      let activity;
      const { criteria } = groupBy;
      if (criteria === 'activity' || criteria === 'activityId') {
        activity = activities.find((ac) => ac.id == t.activityId);
      } else {
        /** If is by responsable we iterate each one, and update it list task */
        if (criteria === 'responsables') {
          return handleUpdatedTasksResponsables(t, updatedTask);
        }
        /** We get the attribute value and filter whole array of parents (activity just to not broke the code) */
        activity = activities.find((ac) => ac.value == t[criteria]);
      }

      const taskToUpdate = getTask(t.id, null, activity);

      if (taskToUpdate.length) {
        taskToUpdate[0].constraints = updatedTask.constraints;
        if (gantt) {
          const ganttRefTask = gantt.getTask(taskToUpdate[0].id);
          if (ganttRefTask) {
            ganttRefTask.constraints = taskToUpdate[0].constraints;
            ganttRefTask.restricted = true;
          }
        }
      }
    });
    await Promise.all(asyncFeature);
    gantt?.callbackAfterAddConstraint?.(updatedTasks);
    updateRender();
  };

  const handleUpdatedTasksResponsables = (t, updatedTask) => {
    activities.map((el) => {
      const taskToUpdate = getTask(t.id, null, el);

      if (taskToUpdate.length) {
        taskToUpdate[0].constraints = updatedTask.constraints;
      }
    });
  };

  return (
    <>
      <ModalAddTags
        t={t}
        Visible={visibleFormTags}
        setVisible={setVisibleFormTags}
      />
      <ConstraintForm
        t={t}
        initialTasks={visibleForm.tasks}
        visibleForm={visibleForm.value}
        setVisibleForm={(val) => setVisibleForm({ value: val, tasks: [] })}
        toSelectResponsables={toSelectResponsables}
        setConstraintAdded={(updatedTasks) => handleUpdatedTasks(updatedTasks)}
      />
      <ModalAddSubContract
        t={t}
        Visible={visibleFormSubcontract}
        setVisible={setVisibleFormSubcontract}
        setSubcontractsReload={setSubcontractsReload}
      />
      <ReportModal
        activities={activities}
        antIcon={antIcon}
        defaultView={defaultView}
        history={history}
        modelReportLookAhead={modelReportLookAhead}
        setModelReportLookAhead={setModelReportLookAhead}
        t={t}
      />
      <LookaheadExportPdfModal
        t={t}
        isVisible={isExportPdfModalVisible}
        setIsVisible={setIsExportPdfModalVisible}
        handleClose={() => setIsExportPdfModalVisible(false)}
        openFitToOnePageModal={() => setIsShowFitToOnePageModal(true)}
      />
      <ModalMessage
        t={t}
        isShow={isShowFitToOnePageModal}
        setIsShow={setIsShowFitToOnePageModal}
      />
      <CardTask
        gantt={gantt}
        updateRender={updateRender}
        activities={activities}
        currentTask={currentTask}
        currentActivity={currentActivity}
        cardState={cardState}
        subContracts={subContracts}
        tableConfig={tableConfig}
        lastLevelActivities={lastLevelActivitiesCard}
        onCloseCard={onCloseCard}
        handshake={handshake}
        toSelectResponsables={toSelectResponsables}
        resources={resourcesCard}
        updateAsyncTask={updateAsyncTask}
        handlePopVisibility={handlePopVisibility}
        popsVisibility={popsVisibility}
        handlePopVisibilityCloseAll={handlePopVisibilityCloseAll}
        t={t}
        permission={permission}
        belongsToRange={belongsToRange}
        dateRange={dateRange}
        setVisibleFormSubcontract={setVisibleFormSubcontract}
        excludeDeep={excludeDeep}
        currentWeekWithPday={currentWeekWithPday}
        updateAsyncActivity={updateAsyncActivity}
        updateResources={updateResources}
        getTask={getTask}
        excludeAfterAction={false}
        deleteSubcontract={deleteSubcontract}
        groupBy={groupBy}
        currentViewLookahead={defaultView}
      />
      <ModalAddResources
        t={t}
        typeResource={typeResource}
        Visible={visibleFormResource}
        setVisible={setVisibleFormResource}
        setReloadTableUsers={setReloadTableUsers}
        activityResource={activityResource}
        setLastLevelActivities={setLastLevelActivitiesCard}
        lastLevelActivities={lastLevelActivitiesCard}
        resourceAdded={(resource) => {
          if (defaultView.current.name === 'gantt')
            return setRefreshResourceForGantt(!refreshResourceForGantt);
          let resourceRoute;
          switch (typeResource) {
            case 'material':
              resourceRoute = 'materialId';
              break;
            case 'rrhh':
              resourceRoute = 'specialtyId';
              break;
            case 'machinery':
              resourceRoute = 'machineId';
              break;
            default:
              resourceRoute = 'materialId';
              break;
          }
          const doesExistAtReference = getTask(
            taskSelected.id,
            null,
            activityResource
          );
          if (doesExistAtReference[0]) {
            const reference = doesExistAtReference[0];
            reference[resourceRoute] = resource.id;
          }
          const saveObject = {
            ...taskSelected,
            [resourceRoute]: resource.id
          };
          updateAsyncTask(saveObject);
          const copy = cloneDeep(taskSelected);
          socket.emit('task_update', {
            sector: projectState.sectorSelected,
            task: copy
          });
        }}
        assignResourceOnCreate={true}
      />
      <Modal
        wrapClassName="activity-modification-style"
        title={modalModification.title}
        visible={modalModification.visible}
        onCancel={() =>
          setModalModification({ ...modalModification, visible: false })
        }
        footer={[
          <Button
            onClick={() =>
              createActivityModificationRequest(
                modalModification.data.task,
                modalModification.data.activity
              )
            }
            key="submit"
            style={{
              background: '#7DFF8A',
              color: '#121212',
              borderColor: '#7DFF8A'
            }}>
            {t('modals.lookahead.activity_modification_request.send_request')}
          </Button>
        ]}>
        {modalModification.visible ? (
          <div>
            <div>
              <span className="name-activity-modification">
                {modalModification.data.activity.name}
              </span>
            </div>
            <div className="dates-container-activity-modification">
              <span className="calendar-activity-modification">
                <img src={calendarModificationIconModal} width={12} />
              </span>
              <span className="dates-activity-modification">
                {modalModification.data.activity.start_date.split(' ')[0]}
              </span>
              <span className="calendar-activity-modification">
                <img src={calendarModificationIconModal} width={12} />
              </span>
              <span className="dates-activity-modification">
                {modalModification.data.activity.end_date.split(' ')[0]}
              </span>
            </div>
            <div className="modification-title-activity-modification">
              {defineTitleModification(modalModification.data.task, t)}
            </div>
            {defineDateUnfit(
              modalModification.data.task,
              modalModification.data.activity,
              t
            )}
            <div className="commentary-container-activity-modification">
              {t('modals.lookahead.activity_modification_request.comment')}
            </div>
            <div>
              <TextArea
                onChange={({ target: { value } }) => {
                  modalModification.description = value;
                }}
                placeholder={t(
                  'modals.lookahead.activity_modification_request.description_placeholder'
                )}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </div>
          </div>
        ) : null}
      </Modal>
      <div />
    </>
  );
}
